import React, { useEffect, useState } from "react";
import {
  Bell,
  CheckCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Checkbox from "src/components/ui/Checkbox";
import Pagination from "src/components/ui/Pagination";
import Skeleton from "src/components/ui/Skeleton";
import PageHeader from "src/components/common/PageHeader";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as adminNotificationActions from "src/redux/slices/adminNotificationSlice";
import {
  AdminNotification,
  AdminNotificationType,
} from "src/types/adminNotification.types";
import { formatDateTime } from "src/utils/date-time";

const TYPE_ICON: Record<AdminNotificationType, React.ElementType> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

const TYPE_ICON_CLASS: Record<AdminNotificationType, string> = {
  info: "text-blue-500 bg-blue-50 dark:bg-blue-900/20",
  success: "text-green-500 bg-green-50 dark:bg-green-900/20",
  warning: "text-amber-500 bg-amber-50 dark:bg-amber-900/20",
  error: "text-red-500 bg-red-50 dark:bg-red-900/20",
};

const NotificationsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { notificationList, unreadCount, markAllRead } = useAppSelector(
    (state) => state.adminNotification
  );

  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const limit = 10;

  const rows: AdminNotification[] = notificationList?.data?.data || [];
  const pagination = notificationList?.data?.pagination;

  const getList = (nextPage: number) =>
    dispatch(
      adminNotificationActions.getNotificationListRequest({
        page: nextPage,
        limit,
        unreadOnly,
      })
    );

  useEffect(() => {
    getList(1);
    setPage(1);
    dispatch(adminNotificationActions.getUnreadCountRequest());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadOnly]);

  useEffect(() => {
    if (markAllRead.data) {
      getList(1);
      setPage(1);
      dispatch(adminNotificationActions.resetMarkAllNotificationsRead());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markAllRead.data]);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    getList(nextPage);
  };

  const handleMarkRead = (notification: AdminNotification) => {
    dispatch(
      adminNotificationActions.markNotificationReadRequest(notification._id)
    );
    if (notification.link) navigate(notification.link);
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Notifications | Invyntra Admin",
        description: "Sign-up requests, support tickets and other alerts.",
        keywords: "notifications, admin, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Notifications"
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Notifications", path: "/admin/notifications" },
          ]}
        />

        <div className="mx-auto max-w-3xl space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
                <Bell size={26} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Notifications
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {unreadCount.data > 0
                    ? `${unreadCount.data} unread notification${
                        unreadCount.data === 1 ? "" : "s"
                      }`
                    : "You're all caught up."}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={() =>
                dispatch(adminNotificationActions.markAllNotificationsReadRequest())
              }
              disabled={markAllRead.loading || unreadCount.data === 0}
            >
              <CheckCheck size={16} className="mr-1.5" />
              Mark all as read
            </Button>
          </div>

          <Card>
            <CardHeader
              title="All notifications"
              description="Sign-up requests, support tickets and workspace alerts."
              actions={
                <Checkbox
                  label="Unread only"
                  checked={unreadOnly}
                  onChange={(e) => setUnreadOnly(e.target.checked)}
                />
              }
            />

            {notificationList.loading && (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div
                    key={`notification-skeleton-${idx}`}
                    className="flex items-start gap-3 rounded-xl border border-gray-100 p-4 dark:border-gray-700"
                  >
                    <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-3.5 w-2/3" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!notificationList.loading && rows.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                <Bell size={28} className="text-gray-300 dark:text-gray-600" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Nothing here yet
                </p>
                <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
                  {unreadOnly
                    ? "No unread notifications right now."
                    : "New sign-up requests and support tickets will show up here."}
                </p>
              </div>
            )}

            {!notificationList.loading && rows.length > 0 && (
              <div className="space-y-3">
                {rows.map((notification) => {
                  const TypeIcon = TYPE_ICON[notification.type] || Info;
                  return (
                    <div
                      key={notification._id}
                      className={`rounded-xl border p-4 ${
                        notification.isRead
                          ? "border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-800"
                          : "border-primary-100 bg-primary-50/50 dark:border-primary-900/40 dark:bg-primary-900/10"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <button
                          className="flex flex-1 items-start gap-3 text-left"
                          onClick={() => handleMarkRead(notification)}
                        >
                          <span
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${TYPE_ICON_CLASS[notification.type]}`}
                          >
                            <TypeIcon size={16} />
                          </span>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {notification.title}
                            </p>
                            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                              {notification.message}
                            </p>
                            <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                              {formatDateTime(notification.createdAt, "D MMM YYYY, HH:mm")}
                            </p>
                          </div>
                        </button>

                        {!notification.isRead && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0"
                            onClick={() => handleMarkRead(notification)}
                          >
                            Mark as read
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!notificationList.loading && pagination && pagination.totalDocs > 0 && (
              <Pagination
                currentPage={page}
                pageLimit={limit}
                totalPages={pagination.totalPages}
                totalRecords={pagination.totalDocs}
                onPageChange={handlePageChange}
              />
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default NotificationsPage;
